// src/components/AnnouncementTicker.tsx
// Header announcement ticker. Shows ONE announcement at a time as a single full
// pass (enters from the right edge, exits off the left), then advances to the
// next. Pauses on hover. Click / "View more" opens a popup with the full text.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Megaphone, X } from 'lucide-react';
import announcementApi from '../api/announcementApi';
import { Announcement } from '../types/announcement';

const REFRESH_MS = 5 * 60 * 1000; // re-poll every 5 min

const fmtDate = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function AnnouncementTicker() {
  const [items, setItems] = useState<Announcement[]>([]);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const reduceMotion = useRef(false);

  useEffect(() => {
    reduceMotion.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
  }, []);

  useEffect(() => {
    let alive = true;
    const fetchActive = async () => {
      try {
        const data = await announcementApi.getActive();
        if (alive) setItems(data);
      } catch {
        if (alive) setItems([]); // silent: never toast a header load failure
      }
    };
    fetchActive();
    const t = setInterval(fetchActive, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Keep the index in range whenever the list changes.
  useEffect(() => { setIdx(0); }, [items.length]);

  // Reduced-motion: no scroll animation fires, so rotate on a timer instead.
  useEffect(() => {
    if (!reduceMotion.current || items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  const current = items.length ? items[idx % items.length] : null;

  // Duration scales with text length so longer messages don't whip past.
  const durationSec = useMemo(() => {
    const len = current?.title?.length || 0;
    return Math.min(90, Math.max(12, Math.round(8 + len * 0.18)));
  }, [current]);

  if (items.length === 0 || !current) return null;

  // Each completed pass advances to the next announcement (one at a time).
  const onPassEnd = () => {
    if (items.length > 1) setIdx((i) => (i + 1) % items.length);
  };

  return (
    <>
      <div
        className="gt-announcement"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        title="View announcement"
      >
        <div className="gt-announcement-viewport">
          <div
            key={idx}
            className="gt-announcement-track"
            style={{ animationDuration: `${durationSec}s` }}
            onAnimationIteration={onPassEnd}
          >
            {current.title}
          </div>
        </div>
        <span className="gt-announcement-more">View more</span>
      </div>

      {open && (
        <div className="gt-announcement-overlay" onClick={() => setOpen(false)}>
          <div className="gt-announcement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="gt-announcement-modal-head">
              <div className="gt-announcement-modal-title">
                <Megaphone size={18} /> <span>Announcements</span>
              </div>
              <button className="gt-announcement-close" onClick={() => setOpen(false)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="gt-announcement-modal-body">
              {items.map((a) => (
                <div className="gt-announcement-card" key={a.id}>
                  <p>{a.title}</p>
                  {a.expires_at && <span className="gt-announcement-meta">Until {fmtDate(a.expires_at)}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
